import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);

/**
 * Global audio player with queue + preview / full modes.
 * mode: 'preview' auto-stops at 30s. mode: 'full' plays whole track and advances queue.
 */
export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [mode, setMode] = useState("preview");
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [t, setT] = useState(0);
  const [queue, setQueue] = useState([]);   // upcoming songs (excluding current)

  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.preload = "metadata";
    const a = audioRef.current;
    const onTime = () => {
      setT(a.currentTime);
      setProgress(a.duration ? a.currentTime / a.duration : 0);
      if (mode === "preview" && a.currentTime >= 30) {
        a.pause();
        setPlaying(false);
      }
    };
    const onLoaded = () => setDuration(a.duration || 0);
    const onEnd = () => {
      setPlaying(false);
      // advance queue when playing full-length
      if (mode === "full") {
        setQueue((q) => {
          if (q.length === 0) return q;
          const [next, ...rest] = q;
          _internalPlay(next, "full");
          return rest;
        });
      }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
    };
    // eslint-disable-next-line
  }, [mode]);

  const _internalPlay = async (song, nextMode) => {
    const a = audioRef.current;
    if (!a) return;
    a.src = song.audio_url;
    setCurrent(song);
    setMode(nextMode);
    try { api.post(`/songs/${song.id}/play`); } catch (_) {}
    try { await a.play(); setPlaying(true); } catch { setPlaying(false); }
  };

  const play = useCallback(async (song, nextMode = "preview") => {
    if (!audioRef.current) return;
    const a = audioRef.current;
    const same = current && current.id === song.id && a.src.includes((song.audio_url||"").split("/").pop() || "___");
    if (!same) {
      await _internalPlay(song, nextMode);
      return;
    }
    setMode(nextMode);
    try { await a.play(); setPlaying(true); } catch { setPlaying(false); }
  }, [current]);

  const playQueue = useCallback(async (songs) => {
    if (!songs || songs.length === 0) return;
    const [first, ...rest] = songs;
    setQueue(rest);
    await _internalPlay(first, "full");
  }, []);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  }, [current]);

  const seek = useCallback((ratio) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const cap = mode === "preview" ? Math.min(30, a.duration) : a.duration;
    a.currentTime = Math.max(0, Math.min(cap, ratio * a.duration));
  }, [mode]);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) { a.pause(); a.currentTime = 0; }
    setPlaying(false);
    setCurrent(null);
    setQueue([]);
  }, []);

  return (
    <PlayerCtx.Provider value={{ current, playing, mode, progress, duration, t, queue, play, playQueue, toggle, seek, stop }}>
      {children}
    </PlayerCtx.Provider>
  );
}
